import React from 'react';
import { motion } from 'framer-motion';
import { Target, Eye, Award, Users } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const About = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      {/* Meet Our Team */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-sm text-[#005782] uppercase tracking-widest font-semibold">
              The People Behind VK Tax Guide
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0F172A] mt-4 mb-4">
              Meet Our Team
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Real people, real experience, and a genuine passion for helping Canadians
            </p>
          </div>

          {/* Varshil Khatri */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            data-testid="founder-varshil"
            className="grid md:grid-cols-5 gap-10 items-start bg-white rounded-3xl p-8 md:p-12 shadow-lg border border-slate-100 mb-12"
          >
            <div className="md:col-span-2">
              <img
                src="https://customer-assets-eiarnc6j.emergentagent.net/job_accounting-sms-hub/artifacts/l95nl5h1_VarshilPhoto.jpeg"
                alt="Varshil Khatri, Founder of VK Tax Guide"
                className="w-full rounded-2xl shadow-xl object-cover aspect-[3/4]"
              />
            </div>
            <div className="md:col-span-3">
              <h3 className="text-3xl font-bold text-[#0F172A] mb-1">Varshil Khatri</h3>
              <p className="text-[#005782] font-semibold mb-6">Founder, VK Tax Guide</p>
              <blockquote className="border-l-4 border-[#005782] pl-5 mb-6">
                <p className="text-xl italic text-slate-700 leading-relaxed">
                  "I know what it feels like to start from zero. That's why I help others build from there."
                </p>
              </blockquote>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  Varshil Khatri came to Canada with a dream of building a better future. Like many newcomers, he had to learn a new system and navigate unfamiliar rules.
                </p>
                <p>
                  He soon realized that taxes could significantly impact families, businesses, and financial goals. He also saw how many people paid more than necessary simply because they didn't know what they could claim or which benefits they qualified for.
                </p>
                <p>
                  That inspired Varshil to make tax knowledge simple, accessible, and trustworthy. What began as helping people with their taxes became VK Tax Guide.
                </p>
                <p>
                  Today, Varshil helps Canadians, newcomers, employees, self-employed professionals, and small business owners understand their taxes and make smarter financial decisions.
                </p>
                <p>
                  For him, every tax return represents more than numbers—it represents someone's hard work, family, business, or dream. Because when you're building a life from the ground up, every dollar matters.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Jigar Darji */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            data-testid="founder-jigar"
            className="grid md:grid-cols-5 gap-10 items-start bg-white rounded-3xl p-8 md:p-12 shadow-lg border border-slate-100"
          >
            <div className="md:col-span-2">
              <img
                src="https://customer-assets-eiarnc6j.emergentagent.net/job_accounting-sms-hub/artifacts/m5pnyz4b_WhatsApp%20Image%202026-09-18%20at%2023.44.16.webp"
                alt="Jigar Darji, Managing Director at VK Tax Guide"
                className="w-full rounded-2xl shadow-xl object-cover aspect-[3/4]"
              />
            </div>
            <div className="md:col-span-3">
              <h3 className="text-3xl font-bold text-[#0F172A] mb-1">Jigar Darji</h3>
              <p className="text-[#005782] font-semibold mb-6">Managing Director</p>
              <blockquote className="border-l-4 border-[#005782] pl-5 mb-6">
                <p className="text-xl italic text-slate-700 leading-relaxed">
                  "Good tax advice starts with asking the right questions."
                </p>
              </blockquote>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  For Jigar Darji, working in Canadian taxation for more than three years has been about much more than numbers, forms, and deadlines.
                </p>
                <p>
                  Over the years, he has had countless conversations with people about their taxes. And one thing became clear—everyone's situation is different. A new job, a growing business, a change in family circumstances, or simply a year of unexpected expenses can all make a difference when it comes to taxes.
                </p>
                <p>
                  Jigar enjoys getting to the bottom of those details. He believes the real value of tax preparation comes from understanding the person behind the return, not just the numbers on it.
                </p>
                <p>
                  That's what made Jigar passionate about tax. He realized that sometimes, helping someone with their taxes isn't just about filing a return—it's about taking the time to understand their situation and making sure they don't leave money behind.
                </p>
                <p>
                  Today, Jigar brings his experience and knowledge to every client, helping Canadians understand their taxes, find opportunities to save, and feel more confident about their finances.
                </p>
                <p>
                  For Jigar, a successful tax return isn't simply one that gets filed on time. It's one where the client feels heard, understood, and confident that their return was handled properly. Because taxes are personal—and every detail deserves to be handled with care.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Mission, Vision & Values */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-[#E0F2FE] rounded-2xl p-8">
              <Target className="w-12 h-12 text-[#005782] mb-4" />
              <h3 className="text-2xl font-bold text-[#0F172A] mb-4">Our Mission</h3>
              <p className="text-slate-700 leading-relaxed">
                To provide Canadians with exceptional tax services that combine expertise, integrity, and personalized attention. We strive to maximize your returns while ensuring full CRA compliance.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-[#E0F2FE] rounded-2xl p-8">
              <Eye className="w-12 h-12 text-[#005782] mb-4" />
              <h3 className="text-2xl font-bold text-[#0F172A] mb-4">Our Vision</h3>
              <p className="text-slate-700 leading-relaxed">
                To be Canada's most trusted tax advisory firm, known for our commitment to client success, innovative solutions, and unwavering dedication to excellence in every filing.
              </p>
            </motion.div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center">
              <div className="w-20 h-20 bg-[#005782] rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-10 h-10 text-white" />
              </div>
              <h4 className="text-xl font-semibold text-[#0F172A] mb-2">Certified Professionals</h4>
              <p className="text-slate-600">
                All our tax specialists are fully certified and continuously trained on the latest tax regulations.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-center">
              <div className="w-20 h-20 bg-[#005782] rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-10 h-10 text-white" />
              </div>
              <h4 className="text-xl font-semibold text-[#0F172A] mb-2">Client-Focused</h4>
              <p className="text-slate-600">
                Your satisfaction is our priority. We provide personalized service tailored to your unique situation.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center">
              <div className="w-20 h-20 bg-[#005782] rounded-full flex items-center justify-center mx-auto mb-4">
                <Target className="w-10 h-10 text-white" />
              </div>
              <h4 className="text-xl font-semibold text-[#0F172A] mb-2">Accuracy Guaranteed</h4>
              <p className="text-slate-600">
                We double-check every return to ensure 100% accuracy and compliance with CRA standards.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
